import { fetchSupabaseRows } from "@/lib/supabaseRest";
import { getAuthSession } from "@/lib/supabaseAuth";
import { getPriceAmount } from "@/utils/price";

const normalize = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s_/-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toArray = (value) => (Array.isArray(value) ? value : []);

let advisorConfigPromise = null;

export const loadAdvisorEngineConfig = async () => {
  if (advisorConfigPromise) return advisorConfigPromise;

  advisorConfigPromise = Promise.all([
    fetchSupabaseRows("advisor_operation_families", "select=*"),
    fetchSupabaseRows("advisor_business_catalog", "select=*&active=eq.true"),
    fetchSupabaseRows("advisor_business_aliases", "select=*"),
    fetchSupabaseRows("advisor_hard_rules", "select=*&active=eq.true"),
    fetchSupabaseRows("advisor_scoring_weights", "select=*&active=eq.true"),
    fetchSupabaseRows(
      "advisor_property_exceptions",
      "select=property_key,capability,allowed,reason,expires_at&active=eq.true"
    ).catch(() => []),
  ])
    .then(([families, businesses, aliases, hardRules, weights, propertyExceptions]) => ({
      families,
      businesses,
      aliases,
      hardRules,
      weights,
      propertyExceptions,
    }))
    .catch((error) => {
      advisorConfigPromise = null;
      throw error;
    });

  return advisorConfigPromise;
};

const getText = (property = {}) => {
  const details = property.details || property.detalles || {};
  return normalize(
    [
      property.title,
      property.subtitle,
      property.address,
      property.location,
      property.description,
      property.servicios,
      property.disponibilidad,
      property.restricciones,
      property.negociosMismaPlaza,
      details.medidas,
      details.frente,
      details.fondo,
      details.metros,
      details.altura,
      details.nivel,
      details.estacionamiento,
      ...(details.ventajasRapidas || []),
    ]
      .filter(Boolean)
      .join(" ")
  );
};

const parseArea = (property = {}) => {
  const details = property.details || property.detalles || {};
  const direct = Number(details.metros || property.metros || 0);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const text = getText(property);
  const match = text.match(/(\d+(?:\.\d+)?)\s*(m2|m²|metros)/);
  return match ? Number(match[1]) : 0;
};

const hasAny = (text, words = []) => words.some((word) => text.includes(normalize(word)));

const hasExplicitVehicleAccess = (text = "") =>
  hasAny(text, [
    "acceso vehicular",
    "entrada vehicular",
    "entrada para vehiculos",
    "entrada para automoviles",
    "puede entrar un auto",
    "pueden entrar vehiculos",
    "entran vehiculos",
    "porton",
    "cortina para vehiculo",
    "patio de maniobra",
    "area de maniobra",
    "espacio de maniobra",
    "carga y descarga",
    "rampa vehicular",
  ]);

export const extractPropertySignals = (property = {}) => {
  const text = getText(property);
  const vision = property.visionAnalysis || property.vision_analysis || {};
  const visionConfidence = Number(vision.confidence || 0);
  const trustVision = visionConfidence >= 0.82;
  const type = normalize(property.type || property.ribbon || "");
  const area = parseArea(property);
  const price = getPriceAmount(property);
  const propertyPublicId = String(property.id || "");
  const propertySubmissionId = String(
    property.submissionId ||
      (propertyPublicId.startsWith("supabase-")
        ? propertyPublicId.replace("supabase-", "")
        : "")
  );
  const restrictions = [];
  const positiveSignals = [];
  const negativeSignals = [];

  const hasUpperFloor = hasAny(text, [
    "planta alta",
    "segundo piso",
    "2do piso",
    "2da planta",
    "nivel 2",
    "piso 2",
    "alta",
  ]);
  const hasGroundFloor = hasAny(text, [
    "planta baja",
    "a pie de calle",
    "a pie de carretera",
    "primer piso",
    "nivel 1",
  ]);
  const hasVehicleAccess =
    hasExplicitVehicleAccess(text) ||
    (trustVision && Boolean(vision.vehicle_entry_visible));

  if (hasAny(text, ["no comida", "no se permite comida", "sin comida"])) restrictions.push("no_comida");
  if (hasAny(text, ["no alcohol", "sin alcohol"])) restrictions.push("no_alcohol");
  if (hasAny(text, ["no ruido", "sin ruido", "no musica", "no música"])) restrictions.push("no_ruido");
  if (hasAny(text, ["no taller", "no talleres"])) restrictions.push("no_taller");
  if (hasAny(text, ["no gas", "sin gas"])) restrictions.push("no_gas");

  if (hasGroundFloor) positiveSignals.push("planta_baja");
  if (hasUpperFloor) negativeSignals.push("planta_alta");
  if (hasAny(text, ["esquina"])) positiveSignals.push("esquina");
  if (hasAny(text, ["avenida", "blvd", "boulevard", "calzada"])) positiveSignals.push("avenida");
  if (hasAny(text, ["doble frente", "dos frentes"])) positiveSignals.push("doble_frente");
  if (hasAny(text, ["estacionamiento", "cajon", "cajón", "cochera"])) positiveSignals.push("estacionamiento");
  if (hasVehicleAccess) positiveSignals.push("acceso_vehicular");

  const realPropertyType =
    type.includes("bodega")
      ? "bodega"
      : type.includes("oficina") || type.includes("consultorio")
      ? "consultorio"
      : type.includes("terreno")
      ? "terreno"
      : type.includes("casa")
      ? "casa"
      : "local";
  const isSmallCommercialPlaza =
    realPropertyType === "local" &&
    (hasAny(text, [
        "local en plaza",
        "local dentro de plaza",
        "plaza comercial",
        "misma plaza",
        "locales de la plaza",
        "locales en plaza",
        "cajones compartidos",
        "estacionamiento compartido",
        "compartidos entre los locales",
      ]) ||
      (trustVision &&
        (vision.commercial_plaza === true || vision.property_context === "commercial_plaza")));

  return {
    property_key: propertySubmissionId ? `supabase-${propertySubmissionId}` : propertyPublicId,
    property_submission_id: propertySubmissionId,
    property_public_id: propertyPublicId,
    property_type: property.type || "",
    real_property_type: realPropertyType,
    floor_signal: hasUpperFloor ? "planta_alta" : hasGroundFloor ? "planta_baja" : "",
    has_ground_floor: hasGroundFloor,
    has_upper_floor: hasUpperFloor,
    has_parking: hasAny(text, ["estacionamiento", "cajon", "cajón", "cochera"]),
    has_vehicle_access: hasVehicleAccess,
    vehicle_entry_confirmed: hasVehicleAccess,
    is_small_commercial_plaza: isSmallCommercialPlaza,
    vision_confidence: visionConfidence,
    vision_evidence: trustVision && Array.isArray(vision.evidence) ? vision.evidence : [],
    has_water: hasAny(text, ["agua"]),
    has_power: hasAny(text, ["luz", "energia", "energía", "220"]),
    has_drainage: hasAny(text, ["drenaje"]),
    has_bathroom: hasAny(text, ["baño", "bano", "medio baño"]),
    has_gas: hasAny(text, ["gas"]),
    has_extraction: hasAny(text, ["extraccion", "extracción", "campana"]),
    is_corner: hasAny(text, ["esquina"]),
    is_avenue: hasAny(text, ["avenida", "blvd", "boulevard", "calzada"]),
    has_double_front: hasAny(text, ["doble frente", "dos frentes"]),
    area_m2: area,
    price,
    restrictions: [...new Set(restrictions)],
    positive_signals: [...new Set(positiveSignals)],
    negative_signals: [...new Set(negativeSignals)],
    raw_text: text,
  };
};

const resolveBusiness = (config, businessQuery = "") => {
  const normalized = normalize(businessQuery);
  if (!normalized) return null;

  const exactAlias = config.aliases.find((item) => {
    const normalizedAlias = normalize(item.normalized_alias || item.alias);
    return normalized === normalizedAlias;
  });
  const containedAlias = config.aliases
    .filter((item) => normalized.includes(normalize(item.normalized_alias || item.alias)))
    .sort(
      (a, b) =>
        normalize(b.normalized_alias || b.alias).length -
        normalize(a.normalized_alias || a.alias).length
    )[0];
  const alias = exactAlias || containedAlias;

  if (alias) {
    return config.businesses.find((business) => business.id === alias.business_id) || null;
  }

  const resolvedByName =
    config.businesses.find((business) => {
      const name = normalize(business.name);
      return normalized === normalize(business.id) || normalized === name || normalized.includes(name);
    }) || null;
  if (resolvedByName) return resolvedByName;

  const fallbackBusinessId =
    hasAny(normalized, [
      "taller mecanico",
      "mecanica automotriz",
      "taller automotriz",
      "frenos de auto",
      "frenos para auto",
      "alineacion automotriz",
      "suspension automotriz",
      "mofles para auto",
      "afinacion automotriz",
    ]) && !hasAny(normalized, ["bicicleta", "bicicletas", "moto", "motos"])
      ? "taller_mecanico"
      : hasAny(normalized, ["llantera", "vulcanizadora"])
      ? "llantera"
      : hasAny(normalized, ["lavado de autos", "car wash", "autolavado"])
      ? "lavado_autos"
      : "";

  return fallbackBusinessId
    ? config.businesses.find((business) => business.id === fallbackBusinessId) || null
    : null;
};

const conditionMatches = (condition = {}, signals = {}) => {
  for (const [key, expected] of Object.entries(condition)) {
    if (key === "restriction") {
      if (!signals.restrictions.includes(expected)) return false;
      continue;
    }
    if (!(key in signals) || signals[key] !== expected) return false;
  }
  return true;
};

const hasActivePropertyException = (
  config,
  signals,
  capability = "vehicle_services_in_commercial_plaza"
) => {
  const now = Date.now();
  return toArray(config.propertyExceptions).some((exception) => {
    if (!exception.allowed || exception.capability !== capability) return false;
    if (String(exception.property_key || "") !== String(signals.property_key || "")) {
      return false;
    }
    if (!exception.expires_at) return true;
    const expiresAt = new Date(exception.expires_at).getTime();
    return Number.isFinite(expiresAt) && expiresAt > now;
  });
};

const getApplicableHardRules = (config, business, signals) => {
  if (!business) return [];
  const familyIds = toArray(business.family_ids);
  const configuredRules = config.hardRules.filter((rule) => {
    if (rule.business_id && rule.business_id !== business.id) return false;
    if (rule.family_id && !familyIds.includes(rule.family_id)) return false;
    return conditionMatches(rule.condition, signals);
  });
  const vehicleServiceBusinesses = new Set([
    "taller_mecanico",
    "llantera",
    "lavado_autos",
  ]);
  const needsVehicleEntryBlock =
    vehicleServiceBusinesses.has(business.id) &&
    signals.is_small_commercial_plaza &&
    !signals.vehicle_service_plaza_exception;

  if (!needsVehicleEntryBlock) return configuredRules;
  if (
    configuredRules.some(
      (rule) =>
        rule.result === "no_apto" &&
        rule.condition?.is_small_commercial_plaza === true
    )
  ) {
    return configuredRules;
  }

  return [
    ...configuredRules,
    {
      id: `${business.id}_plaza_sin_entrada_vehicular`,
      rule_key: `${business.id}_plaza_sin_entrada_vehicular`,
      business_id: business.id,
      family_id: null,
      condition: {
        is_small_commercial_plaza: true,
        vehicle_service_plaza_exception: false,
      },
      result: "no_apto",
      severity: 100,
      user_message: `Esta propiedad forma parte de una plaza comercial destinada a locales de atención al público. La plaza no está autorizada para ${business.name.toLowerCase()} ni para operaciones de mecánica automotriz; por eso este giro no es compatible.`,
      internal_note: "Bloqueo predeterminado para servicios automotrices en plazas comerciales.",
    },
  ];
};

const signalValue = (signal, signals, zoneInsight) => {
  if (signal === "has_water_power") return signals.has_water && signals.has_power;
  if (signal === "residential_signal") return Number(zoneInsight?.residential_signal || zoneInsight?.population?.residentialSignal || 0);
  if (signal in signals) return signals[signal];
  return false;
};

const getApplicableWeights = (config, business, signals, zoneInsight) => {
  const familyIds = business ? toArray(business.family_ids) : [];

  return config.weights.filter((weight) => {
    if (weight.business_id && weight.business_id !== business.id) return false;
    if (weight.family_id && !familyIds.includes(weight.family_id)) return false;

    const value = signalValue(weight.signal, signals, zoneInsight);
    if (typeof value === "boolean") return value;
    if (typeof value === "number") {
      const min = Number(weight.applies_when?.min ?? Number.NEGATIVE_INFINITY);
      const max = Number(weight.applies_when?.max ?? Number.POSITIVE_INFINITY);
      return value >= min && value <= max;
    }
    return Boolean(value);
  });
};

const statusFromScore = (score, hardStatus = "") => {
  if (hardStatus === "no_apto") return "No apto";
  if (hardStatus === "no_recomendado") return "No recomendado";
  if (score >= 85) return "Muy compatible";
  if (score >= 70) return "Compatible";
  if (score >= 55) return "Puede funcionar, validar";
  if (score >= 35) return "Poco recomendado";
  return "No recomendado";
};

const statusCodeFromScore = (score, hardStatus = "") => {
  if (hardStatus === "no_apto") return "no_apto";
  if (hardStatus === "no_recomendado") return "no_recomendado";
  if (score >= 85) return "muy_compatible";
  if (score >= 70) return "compatible";
  if (score >= 55) return "validar";
  if (score >= 35) return "poco_recomendado";
  return "no_recomendado";
};

const getZoneScore = (zoneInsight) => {
  if (!zoneInsight) return 0;
  const direct = Number(
    zoneInsight.directCompetition?.nearbyTotal ??
      zoneInsight.directCompetition?.total ??
      zoneInsight.direct_competitors_1km ??
      0
  );
  const nearby = Number(zoneInsight.otherBusinesses?.total ?? zoneInsight.complementary_businesses ?? 0);
  return Math.min(12, nearby * 1.5) - Math.min(18, direct * 5);
};

const getPropertySpecificRiskReasons = (signals) => {
  const reasons = [];

  if (!signals.is_avenue && !signals.is_corner) {
    reasons.push(
      "En la información publicada no aparece como avenida, boulevard ni esquina; eso puede restar visibilidad y descubrimiento para giros que dependen del paso de clientes."
    );
  } else if (!signals.is_avenue) {
    reasons.push(
      "En la información publicada no aparece sobre avenida o boulevard; valida si el flujo y la visibilidad son suficientes para atraer clientes."
    );
  }

  if (signals.has_upper_floor && !signals.has_ground_floor) {
    reasons.push(
      "El espacio aparece en planta alta o nivel superior; para giros de atención al público puede reducir entrada espontánea y accesibilidad."
    );
  }

  if (!signals.has_parking) {
    reasons.push(
      "No se identifica estacionamiento en la información publicada; puede afectar giros donde el cliente necesita llegar rápido o cargar productos."
    );
  }

  if (
    ["bodega", "terreno"].includes(signals.real_property_type) &&
    !signals.has_vehicle_access
  ) {
    reasons.push(
      "No se confirma acceso vehicular o área de maniobra; para bodega, terreno o carga y descarga conviene validarlo antes de avanzar."
    );
  }

  if (!signals.has_water || !signals.has_power) {
    reasons.push(
      "No se confirman todos los servicios básicos en la información publicada; valida agua y luz antes de tomar una decisión."
    );
  }

  return reasons;
};

const clamp = (value, min = 0, max = 100) =>
  Math.max(min, Math.min(max, Number(value) || 0));

const getDirectCompetitionEvaluation = (total = 0) => {
  const direct = Math.max(0, Number(total) || 0);

  if (direct === 0) {
    return { score: 88, locationCap: 100, finalCap: 98, level: "baja" };
  }
  if (direct <= 3) {
    return {
      score: 84 - direct * 6,
      locationCap: 88,
      finalCap: 84,
      level: "manejable",
    };
  }
  if (direct <= 6) {
    return { score: 48, locationCap: 65, finalCap: 64, level: "alta" };
  }
  if (direct <= 10) {
    return { score: 28, locationCap: 48, finalCap: 49, level: "muy alta" };
  }
  return { score: 12, locationCap: 35, finalCap: 39, level: "saturada" };
};

const DEFAULT_AHP_WEIGHTS = {
  operacion: 30,
  ubicacion: 25,
  inmueble: 20,
  competencia: 15,
  precio: 10,
};

const getAHPWeights = (config, business) => {
  const familyIds = business ? toArray(business.family_ids) : [];
  const selected = config.families.filter((family) => familyIds.includes(family.id));
  if (!selected.length) return DEFAULT_AHP_WEIGHTS;

  const totals = Object.keys(DEFAULT_AHP_WEIGHTS).reduce(
    (result, key) => ({ ...result, [key]: 0 }),
    {}
  );
  selected.forEach((family) => {
    Object.keys(totals).forEach((key) => {
      totals[key] += Number(family.default_weights?.[key] ?? DEFAULT_AHP_WEIGHTS[key]);
    });
  });

  const averages = Object.fromEntries(
    Object.entries(totals).map(([key, value]) => [key, value / selected.length])
  );
  const sum = Object.values(averages).reduce((total, value) => total + value, 0) || 100;
  return Object.fromEntries(
    Object.entries(averages).map(([key, value]) => [key, Number(((value / sum) * 100).toFixed(2))])
  );
};

const getAHPBreakdown = ({ config, business, signals, zoneInsight, matchedWeights }) => {
  const directCompetitors = Number(
    zoneInsight?.directCompetition?.nearbyTotal ??
      zoneInsight?.directCompetition?.total ??
      zoneInsight?.direct_competitors_1km ??
      0
  );
  const nearbyBusinesses = Number(
    zoneInsight?.otherBusinesses?.total ??
      zoneInsight?.otherBusinesses?.length ??
      zoneInsight?.complementary_businesses ??
      0
  );
  const minArea = Number(business?.min_area_m2 || 0);
  const idealArea = Number(business?.ideal_area_m2 || minArea || 0);
  const hasCoreServices = signals.has_water && signals.has_power;
  const competitionEvaluation = getDirectCompetitionEvaluation(directCompetitors);

  let operacion = 55;
  if (signals.has_ground_floor) operacion += 8;
  if (signals.has_vehicle_access) operacion += 8;
  if (hasCoreServices) operacion += 8;
  if (signals.has_drainage) operacion += 5;
  if (signals.has_bathroom) operacion += 4;
  operacion -= signals.restrictions.length * 12;

  let ubicacion = 50;
  if (signals.is_corner) ubicacion += 16;
  if (signals.is_avenue) ubicacion += 13;
  if (signals.has_double_front) ubicacion += 9;
  if (signals.has_parking) ubicacion += 7;
  ubicacion += Math.min(12, nearbyBusinesses * 1.5);
  ubicacion = Math.min(ubicacion, competitionEvaluation.locationCap);

  let inmueble = 55;
  if (signals.has_parking) inmueble += 6;
  if (signals.has_vehicle_access) inmueble += 7;
  if (signals.area_m2 && minArea) {
    if (signals.area_m2 < minArea) inmueble -= 30;
    else if (idealArea && signals.area_m2 >= idealArea) inmueble += 18;
    else inmueble += 9;
  }

  const competencia = competitionEvaluation.score;

  let precio = 60;
  if (signals.price > 0 && signals.price <= 8000) precio += 12;
  else if (signals.price > 30000) precio -= 8;

  const dimensionBySignal = {
    has_water_power: "operacion",
    has_drainage: "operacion",
    has_bathroom: "operacion",
    has_ground_floor: "operacion",
    has_upper_floor: "operacion",
    has_vehicle_access: "inmueble",
    has_parking: "ubicacion",
    is_corner: "ubicacion",
    is_avenue: "ubicacion",
    has_double_front: "ubicacion",
    area_m2: "inmueble",
    residential_signal: "ubicacion",
  };
  matchedWeights.forEach((weight) => {
    const dimension = dimensionBySignal[weight.signal] || "operacion";
    const adjustment = Number(weight.weight || 0) * 0.45;
    if (dimension === "operacion") operacion += adjustment;
    if (dimension === "ubicacion") ubicacion += adjustment;
    if (dimension === "inmueble") inmueble += adjustment;
  });

  const scores = {
    operacion: clamp(operacion),
    ubicacion: clamp(ubicacion),
    inmueble: clamp(inmueble),
    competencia: clamp(competencia),
    precio: clamp(precio),
  };
  const weights = getAHPWeights(config, business);
  const total = Object.keys(scores).reduce(
    (sum, key) => sum + scores[key] * (Number(weights[key]) / 100),
    0
  );

  return {
    method: "AHP",
    weights,
    scores,
    total: clamp(Math.round(total)),
    directCompetition: {
      total: directCompetitors,
      level: competitionEvaluation.level,
      finalCap: competitionEvaluation.finalCap,
    },
  };
};

export const evaluateAdvisorMatch = async ({
  property,
  businessQuery,
  zoneInsight = null,
  allProperties = [],
}) => {
  const config = await loadAdvisorEngineConfig();
  return evaluateAdvisorMatchWithConfig({
    config,
    property,
    businessQuery,
    zoneInsight,
    allProperties,
  });
};

export const evaluateAdvisorMatchWithConfig = ({
  config,
  property,
  businessQuery,
  zoneInsight = null,
  allProperties = [],
}) => {
  const business = resolveBusiness(config, businessQuery);
  const baseSignals = extractPropertySignals(property);
  const signals = {
    ...baseSignals,
    vehicle_service_plaza_exception: hasActivePropertyException(config, baseSignals),
  };
  const hardRules = getApplicableHardRules(config, business, signals).sort((a, b) => b.severity - a.severity);
  const hardBlock = hardRules.find((rule) => rule.result === "no_apto");
  const hardNoRecommended = hardRules.find((rule) => rule.result === "no_recomendado" || rule.result === "bloqueado");
  const weights = business ? getApplicableWeights(config, business, signals, zoneInsight) : [];
  const positiveReasons = [];
  const negativeReasons = [];
  const ahp = getAHPBreakdown({
    config,
    business,
    signals,
    zoneInsight,
    matchedWeights: weights,
  });

  if (hardBlock) {
    ahp.scores.operacion = Math.min(ahp.scores.operacion, 15);
  } else if (hardNoRecommended) {
    ahp.scores.operacion = Math.min(ahp.scores.operacion, 40);
  }

  let score = ahp.total;
  const directCompetition = ahp.directCompetition || {
    total: 0,
    level: "baja",
    finalCap: 98,
  };
  weights.forEach((weight) => {
    if (weight.reason_template) {
      if (Number(weight.weight) >= 0) positiveReasons.push(weight.reason_template);
      else negativeReasons.push(weight.reason_template);
    }
  });

  negativeReasons.push(...getPropertySpecificRiskReasons(signals));

  if (business?.min_area_m2 && signals.area_m2 && signals.area_m2 < Number(business.min_area_m2)) {
    score -= 18;
    negativeReasons.push(
      `La propiedad tiene ${signals.area_m2} m² y ${business.name} normalmente necesita al menos ${business.min_area_m2} m² para trabajar con seguridad y comodidad.`
    );
  }

  score += getZoneScore(zoneInsight) * 0.2;

  if (directCompetition.total >= 4) {
    negativeReasons.push(
      `Se detectaron ${directCompetition.total} competidores directos en 1 km. Para este giro puede indicar saturación si el mercado cercano no alcanza para otra opción similar; valida flujo, población cercana y diferenciador antes de avanzar. En algunos giros, una concentración alta puede ser positiva si la zona ya es reconocida como corredor especializado.`
    );
  }

  hardRules.forEach((rule) => {
    if (rule.result === "no_apto" || rule.result === "no_recomendado" || rule.result === "bloqueado") {
      negativeReasons.unshift(rule.user_message);
    } else {
      negativeReasons.push(rule.user_message);
    }
  });

  if (hardBlock) score = Math.min(score, 24);
  else if (hardNoRecommended) score = Math.min(score, 44);

  const finalScore = Math.max(
    0,
    Math.min(98, directCompetition.finalCap, Math.round(score))
  );
  const hardStatus = hardBlock
    ? "no_apto"
    : hardNoRecommended
    ? "no_recomendado"
    : "";
  const alternatives = buildAdvisorAlternatives({
    config,
    business,
    businessQuery,
    currentProperty: property,
    currentSignals: signals,
    allProperties,
  });

  return {
    business,
    signals,
    percentage: finalScore,
    tone: statusFromScore(finalScore, hardStatus),
    status: statusCodeFromScore(finalScore, hardStatus),
    ahp,
    hardRuleHits: hardRules,
    reasons: [...new Set(positiveReasons)].slice(0, 4),
    risks: [...new Set(negativeReasons)].slice(0, 4),
    alternatives,
  };
};

export const logAdvisorDecision = async ({
  property,
  businessQuery,
  zoneInsight,
  advisorResult,
  coordinates,
}) => {
  if (!property || !businessQuery || !advisorResult) return false;

  const session = getAuthSession();
  const { raw_text: _rawText, ...signals } = advisorResult.signals || {};
  const payload = {
    property_submission_id: property.submissionId || property.id,
    property_public_id: property.id,
    business_id: advisorResult.business?.id || null,
    business_query: businessQuery,
    score: advisorResult.percentage,
    status: advisorResult.status,
    hard_rule_hits: (advisorResult.hardRuleHits || []).map((rule) => ({
      id: rule.id,
      result: rule.result,
      severity: rule.severity,
      message: rule.user_message,
    })),
    positive_reasons: advisorResult.reasons || [],
    negative_reasons: advisorResult.risks || [],
    alternatives: advisorResult.alternatives || [],
    inputs_used: {
      coordinates,
      signals,
      ahp: advisorResult.ahp || null,
      zone_summary: {
        direct_competition:
          zoneInsight?.directCompetition?.nearbyTotal ??
          zoneInsight?.directCompetition?.total ??
          null,
        nearby_businesses: zoneInsight?.otherBusinesses?.total ?? null,
        confidence: zoneInsight?.confidence || zoneInsight?.dataConfidence || null,
      },
    },
  };

  try {
    const response = await fetch("/api/advisor-decision-log", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    return response.ok;
  } catch (error) {
    console.log("No se pudo guardar el historial del asesor:", error);
    return false;
  }
};

const getLocationText = (property = {}) => normalize(`${property.location || ""} ${property.address || ""}`);

const getPropertyCoordinates = (property = {}) => {
  const lat = Number(property.lat);
  const lng = Number(property.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  const mapUrl = decodeURIComponent(property.mapUrl || "");
  const match = mapUrl.match(/q=([-0-9.]+)\s*,\s*([-0-9.]+)/);
  return match ? { lat: Number(match[1]), lng: Number(match[2]) } : null;
};

const getDistanceKm = (from, to) => {
  if (!from || !to) return null;
  const radians = (degrees) => (degrees * Math.PI) / 180;
  const earthKm = 6371;
  const latDelta = radians(to.lat - from.lat);
  const lngDelta = radians(to.lng - from.lng);
  const value = Math.sin(latDelta / 2) ** 2 +
    Math.cos(radians(from.lat)) * Math.cos(radians(to.lat)) * Math.sin(lngDelta / 2) ** 2;
  return earthKm * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
};

const buildAdvisorAlternatives = ({ config, business, businessQuery, currentProperty, currentSignals, allProperties }) => {
  if (!business || !Array.isArray(allProperties) || allProperties.length <= 1) return [];
  const currentLocation = getLocationText(currentProperty);
  const currentPrice = getPriceAmount(currentProperty);
  const currentCoordinates = getPropertyCoordinates(currentProperty);

  return allProperties
    .filter((property) => property.id !== currentProperty.id)
    .map((property) => {
      const signals = extractPropertySignals(property);
      const hardRules = getApplicableHardRules(config, business, signals);
      if (hardRules.some((rule) => rule.result === "no_apto")) return null;

      const weights = getApplicableWeights(config, business, signals, null);
      let score = getAHPBreakdown({
        config,
        business,
        signals,
        zoneInsight: null,
        matchedWeights: weights,
      }).total;
      if (business.min_area_m2 && signals.area_m2 && signals.area_m2 < Number(business.min_area_m2)) score -= 18;
      if (hardRules.some((rule) => rule.result === "no_recomendado" || rule.result === "bloqueado")) score = Math.min(score, 44);

      const location = getLocationText(property);
      const sameZone = currentLocation && location && (location.includes(currentLocation) || currentLocation.includes(location));
      const distance = getDistanceKm(currentCoordinates, getPropertyCoordinates(property));
      const price = getPriceAmount(property);
      const priceDifference = currentPrice && price ? price - currentPrice : 0;
      const locationBoost = sameZone ? 22 : distance !== null && distance <= 2 ? 18 : distance !== null && distance <= 5 ? 12 : distance !== null && distance <= 10 ? 6 : 0;
      const priceBoost = !currentPrice || !price ? 0 : priceDifference <= 0 ? 8 : priceDifference <= 7000 ? 5 : priceDifference <= 15000 ? 0 : -6;
      const typeBoost = property.type === currentProperty.type ? 5 : 0;
      // La compatibilidad con el giro manda; ubicación y precio solo desempatan.
      const ranking = Math.round(score * 10 + locationBoost + priceBoost + typeBoost);

      return {
        property,
        percentage: Math.max(0, Math.min(98, Math.round(score))),
        ranking,
        sameZone,
        distanceKm: distance === null ? null : Number(distance.toFixed(1)),
        price,
        reason:
          sameZone
            ? "Está en una zona parecida y tiene mejor compatibilidad para este giro."
            : "Puede funcionar mejor para este giro aunque cambie la ubicación o precio.",
      };
    })
    .filter((item) => item && item.percentage >= 55)
    .sort((a, b) => b.ranking - a.ranking);
};
